import { useState, useEffect, useCallback } from "react";
import { useAdminApi } from "../adminUtils";
import "../admin-base.css";
import "../admin-layout.css";
import "../admin-controls.css";
import "../admin-students.css";
import "../admin-misc.css";
import "../admin-responsive.css";

// What a code can be used on. Memberships, private passes and workshops don't take codes,
// so they aren't offered here (10-17).
const PASS_TYPES = [
  { value: "dropin",    label: "Class drop-in" },
  { value: "pass4",     label: "4-Class Pass" },
  { value: "pass8",     label: "8-Class Pass" },
  { value: "video_rental", label: "Video rental" },
];

const EMPTY_FORM = {
  code: "",
  type: "percent",
  amount: "",
  appliesToTypes: [],
  maxUses: "",
  expiresAt: "",
  allowedMemberships: [],
  allowedEmails: "",
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const MEMBERSHIP_OPTIONS = [
  { value: "memberUnl", label: "Monthly" },
  { value: "member2x",  label: "2x/week" },
];

function couponMemberships(c) {
  if (c.allowedMemberships?.length) return c.allowedMemberships;
  return c.membersOnly ? MEMBERSHIP_OPTIONS.map(o => o.value) : [];
}

function accessLabel(c) {
  const parts = [];
  const ms = couponMemberships(c);
  if (ms.length) parts.push(ms.map(m => MEMBERSHIP_OPTIONS.find(o => o.value === m)?.label || m).join(" + "));
  const n = c.allowedEmails?.length || 0;
  if (n) parts.push(`${n} email${n === 1 ? "" : "s"}`);
  return parts.length ? ` · ${parts.join(", ")}` : "";
}

export default function CouponsTab() {
  const { API, AUTH } = useAdminApi();
  const [coupons, setCoupons]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  const [editingId, setEditingId] = useState(null);

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/coupons`, { headers: AUTH });
      const data = await res.json();
      setCoupons(data.coupons ?? []);
    } catch { setCoupons([]); }
    finally { setLoading(false); }
  }, [API, AUTH]);

  useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

  const toggleType = (val) => {
    setForm(f => ({
      ...f,
      appliesToTypes: f.appliesToTypes.includes(val)
        ? f.appliesToTypes.filter(t => t !== val)
        : [...f.appliesToTypes, val],
    }));
  };

  const handleAdd = async () => {
    if (!form.code.trim() || !form.amount) { setError("Code and amount are required."); return; }
    setSaving(true); setError(null);
    try {
      const payload = {
        type: form.type,
        amount: Number(form.amount),
        appliesToTypes: form.appliesToTypes,
        maxUses: Number(form.maxUses) || 0,
        expiresAt: form.expiresAt || null,
        allowedMemberships: form.allowedMemberships,
        allowedEmails: form.allowedEmails,
      };
      const res = editingId
        ? await fetch(`${API}/coupons/${editingId}`, { method: "PATCH", headers: AUTH, body: JSON.stringify(payload) })
        : await fetch(`${API}/coupons`, { method: "POST", headers: AUTH, body: JSON.stringify({ code: form.code.trim(), ...payload }) });
      const data = await res.json();
      if (!res.ok) { setError(data.error ?? "Failed."); return; }
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      fetchCoupons();
    } catch { setError("Something went wrong."); }
    finally { setSaving(false); }
  };

  const startEdit = (c) => {
    setEditingId(c._id);
    setForm({
      code: c.code,
      type: c.type,
      amount: String(c.amount),
      appliesToTypes: c.appliesToTypes || [],
      maxUses: c.maxUses ? String(c.maxUses) : "",
      expiresAt: c.expiresAt ? c.expiresAt.slice(0, 10) : "",
      allowedMemberships: couponMemberships(c),
      allowedEmails: (c.allowedEmails || []).join("\n"),
    });
    setError(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm("Deactivate this coupon?")) return;
    await fetch(`${API}/coupons/${id}/deactivate`, { method: "PATCH", headers: AUTH });
    fetchCoupons();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Permanently delete this coupon?")) return;
    await fetch(`${API}/coupons/${id}`, { method: "DELETE", headers: AUTH });
    fetchCoupons();
  };

  return (
    <div className="adm-tab-content">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem" }}>
        <h2 style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--color-grey-600)" }}>Discount Codes</h2>
        {!showForm && (
          <button className="adm-btn--primary" onClick={() => { setEditingId(null); setForm(EMPTY_FORM); setShowForm(true); }}>+ New Code</button>
        )}
      </div>

      {/* Add form */}
      {showForm && (
        <div className="adm-settings-section" style={{ marginBottom: "1.5rem" }}>
          <h3 className="adm-settings-heading">{editingId ? `Edit ${form.code}` : "New Discount Code"}</h3>
          <div className="adm-form-grid" style={{ maxWidth: "560px" }}>
            <label className="adm-form-label">Code
              <input className="adm-input" value={form.code} disabled={!!editingId}
                onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="e.g. WELCOME10" />
            </label>
            <label className="adm-form-label">Type
              <select className="adm-input" value={form.type}
                onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                <option value="percent">Percent off (%)</option>
                <option value="fixed">Fixed amount ($)</option>
              </select>
            </label>
            <label className="adm-form-label">
              {form.type === "percent" ? "Discount (%)" : "Discount ($)"}
              <input className="adm-input" type="number" min="0" step={form.type === "percent" ? "1" : "0.01"}
                value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                placeholder={form.type === "percent" ? "e.g. 10" : "e.g. 5.00"} />
            </label>
            <label className="adm-form-label">Max uses (0 = unlimited)
              <input className="adm-input" type="number" min="0" value={form.maxUses}
                onChange={e => setForm(f => ({ ...f, maxUses: e.target.value }))}
                placeholder="0" />
            </label>
            <label className="adm-form-label" style={{ gridColumn: "1/-1" }}>Expiry date (optional)
              <input className="adm-input" type="date" value={form.expiresAt}
                onChange={e => setForm(f => ({ ...f, expiresAt: e.target.value }))} />
            </label>
          </div>

          <div style={{ marginTop: "1rem" }}>
            <p className="adm-form-label" style={{ marginBottom: "0.5rem" }}>Applies to (leave all unchecked = drop-ins and passes; video rentals must be ticked)</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
              {PASS_TYPES.map(pt => (
                <label key={pt.value} style={{ display: "flex", alignItems: "center", gap: "0.4rem",
                  fontSize: "0.85rem", cursor: "pointer" }}>
                  <input type="checkbox"
                    checked={form.appliesToTypes.includes(pt.value)}
                    onChange={() => toggleType(pt.value)} />
                  {pt.label}
                </label>
              ))}
            </div>
          </div>

          <div style={{ marginTop: "1rem" }}>
            <p className="adm-form-label" style={{ marginBottom: "0.5rem" }}>
              Who can use it — video rentals (leave both empty = anyone)
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", marginBottom: "0.5rem" }}>
              {MEMBERSHIP_OPTIONS.map(m => (
                <label key={m.value} style={{ display: "flex", alignItems: "center", gap: "0.4rem",
                  fontSize: "0.85rem", cursor: "pointer" }}>
                  <input type="checkbox"
                    checked={form.allowedMemberships.includes(m.value)}
                    onChange={() => setForm(f => ({
                      ...f,
                      allowedMemberships: f.allowedMemberships.includes(m.value)
                        ? f.allowedMemberships.filter(x => x !== m.value)
                        : [...f.allowedMemberships, m.value],
                    }))} />
                  Active {m.label} members
                </label>
              ))}
            </div>
            <label className="adm-form-label">Specific emails (one per line, or comma-separated)
              <textarea className="adm-input" rows={4} value={form.allowedEmails}
                onChange={e => setForm(f => ({ ...f, allowedEmails: e.target.value }))}
                placeholder={"jane@example.com\nsam@example.com"} />
            </label>
          </div>

          {error && <p className="adm-error" style={{ marginTop: "0.75rem" }}>{error}</p>}

          <div className="adm-modal-actions" style={{ justifyContent: "flex-start", marginTop: "1rem" }}>
            <button className="adm-btn-export" onClick={() => { setShowForm(false); setError(null); setForm(EMPTY_FORM); setEditingId(null); }}>Cancel</button>
            <button className="adm-btn--primary" onClick={handleAdd} disabled={saving}>
              {saving ? "Saving…" : editingId ? "Save Changes" : "Create Code"}
            </button>
          </div>
        </div>
      )}

      {/* Coupons table */}
      {loading ? (
        <p className="adm-empty">Loading…</p>
      ) : coupons.length === 0 ? (
        <p className="adm-empty">No discount codes yet.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="adm-report-table" style={{ width: "100%", minWidth: "640px" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>Code</th>
                <th style={{ textAlign: "left" }}>Discount</th>
                <th style={{ textAlign: "left" }}>Applies to</th>
                <th style={{ textAlign: "right" }}>Uses</th>
                <th style={{ textAlign: "left" }}>Expires</th>
                <th style={{ textAlign: "left" }}>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {coupons.map(c => (
                <tr key={c._id}>
                  <td><strong>{c.code}</strong></td>
                  <td>{c.type === "percent" ? `${c.amount}%` : `$${c.amount.toFixed(2)}`}</td>
                  <td style={{ fontSize: "0.8rem", color: "var(--color-grey-400)" }}>
                    {c.appliesToTypes.length === 0 ? "All" : c.appliesToTypes.join(", ")}{accessLabel(c)}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {c.usedCount}{c.maxUses > 0 ? ` / ${c.maxUses}` : ""}
                  </td>
                  <td>{fmtDate(c.expiresAt)}</td>
                  <td>
                    <span style={{
                      fontSize: "0.75rem", fontWeight: 600, padding: "0.2rem 0.5rem",
                      borderRadius: "4px",
                      background: c.active ? "var(--color-primary)" : "var(--color-grey-200)",
                      color: c.active ? "var(--color-tertiary)" : "var(--color-grey-400)",
                    }}>
                      {c.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button className="adm-btn-ghost-sm" style={{ marginRight: "0.5rem" }}
                      onClick={() => startEdit(c)}>
                      Edit
                    </button>
                    {c.active && (
                      <button className="adm-btn-ghost-sm" style={{ marginRight: "0.5rem" }}
                        onClick={() => handleDeactivate(c._id)}>
                        Deactivate
                      </button>
                    )}
                    <button className="adm-btn-danger-sm" onClick={() => handleDelete(c._id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
